import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faChevronLeft, faCheck, faTimes, faSync, faShare, faFileAlt, faExternalLinkAlt, faGavel, faClock } from '@fortawesome/free-solid-svg-icons';
import { jsPDF } from 'jspdf';
import indIcon from '../../assets/Ind.png';
import bikeImage from '../../assets/bikeImage.jpg';
import carImage from '../../assets/carImage.png';
import scooterImage from '../../assets/scooterImage.png';
import truckImage from '../../assets/truckImage.jpg';
import styles from './ChallanDetails.module.css';
import FAQSection from '../../components/FAQSection';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.rtoxpert.com';

function ChallanDetails() {
  const location = useLocation();
  const navigate = useNavigate();
  const [vehicleNumber, setVehicleNumber] = useState(location.state?.vehicleNumber || 'MH 15 CV 2211');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [addingToGarage, setAddingToGarage] = useState(false);
  const [showRCModal, setShowRCModal] = useState(false);
  const [showPaymentSuccessModal, setShowPaymentSuccessModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPayAllModal, setShowPayAllModal] = useState(false);
  const [currentChallanId, setCurrentChallanId] = useState(null);
  const [vehicleDetails, setVehicleDetails] = useState(location.state?.vehicleDetails || null);
  const [challanData, setChallanData] = useState(location.state?.challanDetails || null);
  const [pendingChallans, setPendingChallans] = useState([]);
  const [paidChallans, setPaidChallans] = useState([]);
  const [agreedToTerms, setAgreedToTerms] = useState({});
  const [paidChallanForModal, setPaidChallanForModal] = useState(null);

  // New state for mobile app-style UI
  const [activeTab, setActiveTab] = useState('pending'); // 'pending' or 'paid'
  const [selectedChallans, setSelectedChallans] = useState({});
  const [expandedChallans, setExpandedChallans] = useState({});

  const toggleExpand = (challanId) => {
    setExpandedChallans(prev => ({ ...prev, [challanId]: !prev[challanId] }));
  };

  // Generate and download eChallan PDF
  const downloadChallanPDF = (challan) => {
    const doc = new jsPDF();
    const vehNum = vehicleDetails?.registration_number || vehicleDetails?.document_id || vehicleNumber;

    // Header
    doc.setFillColor(59, 130, 246);
    doc.rect(0, 0, 210, 35, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('eCHALLAN DETAILS', 105, 18, { align: 'center' });
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Generated via VaahanBuddy', 105, 28, { align: 'center' });

    // Reset text color
    doc.setTextColor(0, 0, 0);
    let y = 50;

    // Vehicle info section
    doc.setFillColor(240, 244, 255);
    doc.roundedRect(15, y - 5, 180, 25, 3, 3, 'F');
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Vehicle Number:', 20, y + 5);
    doc.setFont('helvetica', 'normal');
    doc.text(String(vehNum || 'N/A'), 75, y + 5);
    doc.setFont('helvetica', 'bold');
    doc.text('Accused Name:', 20, y + 15);
    doc.setFont('helvetica', 'normal');
    doc.text(String(challan.accusedName || 'N/A'), 75, y + 15);
    y += 35;

    // Challan details
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(59, 130, 246);
    doc.text('Challan Information', 20, y);
    doc.setTextColor(0, 0, 0);
    y += 10;

    const details = [
      ['Challan Number', challan.id || 'N/A'],
      ['Issue Date', challan.issueDate || 'N/A'],
      ['Offence', challan.offence || 'N/A'],
      ['Location', challan.area || 'N/A'],
      ['State', challan.state || 'N/A'],
      ['Challan Type', challan.challan_type || 'N/A'],
      ['Challan Amount', `Rs. ${challan.amount?.toLocaleString() || '0'}`],
      ['Service Fee', `Rs. ${challan.service_fee?.toFixed(2) || '0'}`],
      ['Total Amount', `Rs. ${challan.total_amount?.toLocaleString() || '0'}`],
    ];

    details.forEach(([label, value], i) => {
      if (i % 2 === 0) {
        doc.setFillColor(248, 250, 252);
        doc.rect(15, y - 4, 180, 10, 'F');
      }
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text(label + ':', 20, y + 3);
      doc.setFont('helvetica', 'normal');
      // Wrap long text
      const maxWidth = 100;
      const lines = doc.splitTextToSize(String(value), maxWidth);
      doc.text(lines, 80, y + 3);
      y += 10 * lines.length;
      if (y > 260) { doc.addPage(); y = 20; }
    });

    // Footer
    y += 10;
    doc.setDrawColor(200, 200, 200);
    doc.line(15, y, 195, y);
    y += 10;
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text('This is a system-generated document from VaahanBuddy.', 105, y, { align: 'center' });
    doc.text('For official records, please refer to the Traffic Department portal.', 105, y + 5, { align: 'center' });
    doc.text(`Generated on: ${new Date().toLocaleString('en-IN')}`, 105, y + 10, { align: 'center' });

    doc.save(`eChallan_${challan.id || 'details'}.pdf`);
  };

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const termsAndConditions = `
**Terms and Conditions**
"VAAHANBUDDY" through third parties engaged by it ("Legal Partner") enables an individual (hereinafter referred to as "You", "Your" or "User"), to pay his/her online or court challans. VAAHANBUDDY merely acts as a facilitator that connects you to the Legal Partner for payment of challans and the Legal Partner is solely responsible for completing the payment/settlement of challans (whether online or court challans). By availing payment/settlement of challans through VAAHANBUDDY platform you hereby agree and appoint the Legal Partner as your agent to make payments of the challans issued in your name and appear and represent you before traffic court in case of court challans. By availing the services of paying your challans through VAAHANBUDDY platform you hereby agree to be bound by these terms and conditions.
You hereby understand and agree that online challans may take up to 10-12 working days for settlement from the date of receipt of all information and documents related to the challan by VAAHANBUDDY and Court challans may take up to 60-90 working days for settlement. In case of any delay or failure in the settlement of your challan, an automatic refund will be processed within 7 working days after the stipulated time by the legal advisor.
Refunds usually credited in 7-10 working days in the users account in case of further delays, kindly contact your bank representative.
In case your challan is settled at a lower value than your original challan amount, the balance challan amount shall be refunded back to you within 7 working days from the date of confirmation along with the valid payment receipt.
Refunds are usually credited in 7-10 business days in the users account in case of further delays, kindly contact your bank representative. You understand and agree that VAAHANBUDDY is not responsible for delays or denials in processing payments at the bank's end.
If you have submitted your challan to VAAHANBUDDY for settlement but subsequently chose to have it resolved through an external legal advisor on your own, no refund will be issued to you against court challans.
You understand that the time it takes to reflect the final settlement status on the Traffic department portal varies between 30 to 60 working days in case of Traffic court challans and 10 to 15 working days in case of online Traffic challans. Any delay in updation of final settlement status on the traffic department portal shall not be attributable to VAAHANBUDDY and we shall not be responsible for any such delays. Any dispute regarding non-updation of challan status on the Traffic portal, you can raise a ticket on VAAHANBUDDY customer support support@VAAHANBUDDY.app within 30 days post stipulated timelines.
You hereby agree and acknowledge that the timelines provided herein for settlement/payment of court and online challans are only estimates for reference purposes and are subject to court holidays, court strikes and other factors outside the control of VAAHANBUDDY. While VAAHANBUDDY through its Legal Partner may attempt to settle/pay the challans within the stipulated timelines, VAAHANBUDDY does not guarantee or make any representations that the challans shall be settled/paid within the stipulated timelines.
You understand and agree that the final outcome of settlement of the court challans or any subsequent action ordered by the traffic courts are outside the control and purview of VAAHANBUDDY and at the sole discretion of the relevant court. You shall not hold VAAHANBUDDY or any third party service provider engaged by it responsible for any adverse action or order issued against you by the traffic court on the court challan.
VAAHANBUDDY, either itself or through the Legal Partner will provide you with the payment receipts in case of Traffic Court, Lok Adalat and Online challans within the stipulated timelines.
In Lok Adalat matters, challan payment success cannot be guaranteed due to the token-based system and in such cases as an alternative the third party service provider shall pay challans if the challans are present in virtual court.
VAAHANBUDDY along with the Legal Partners disclaims any liability or responsibility in case the traffic challans get rejected by the Traffic Department, in such cases a full refund shall be processed within 7 days of rejection.
In instances where challan payment receipts are unavailable, you will be provided with a declaration or screenshot from the relevant Government portal as proof of payment status.
You shall be solely responsible for any rejection by the Traffic department in case of any variation related to the documents submitted by you to VAAHANBUDDY or the Legal Partner and further resulting in any non-compliance against such pending challans.
In case of any mismatch regarding your challan information and status on VAAHANBUDDY platform, you are Detective to update the status to get the correct information reflected by entering a valid OTP from the Traffic Challan Portal.
Any liability against the incorrect information reflected on VAAHANBUDDY platform and challans payment due to non-updation shall be sole responsibility of the User.
The services are provided by the Legal Partner and the Legal Partner is solely entrusted with the responsibility to pay traffic challans and VAAHANBUDDY shall not be liable for any inactions by the Legal Partner.
The VAAHANBUDDY platform and the challan payment services are offered on an as-is basis and VAAHANBUDDY makes no implied warranties, with respect to the services. All implied warranties of merchantability and fitness for a particular purpose or use, are disclaimed. No one is authorized to make any warranty on VAAHANBUDDY's behalf, and users may not rely on any other statement of warranty.
You hereby acknowledge and agree that to facilitate payment of your challans, VAAHANBUDDY will be required to share your personal data including without limitation your traffic challan details, including phone numbers and all required documents, with the Legal Partner, accordingly you hereby consent to sharing of such information with the Legal Partner.
The Legal Partner will abstain from paying your challan if you are required to appear in Court. Any failure to settle the Challan in such cases shall be sole responsibility of the User.
The Legal Partner will refrain from paying your challan if the challan payment amount exceeds the order amount. In such cases full refund shall be automatically processed within 7 working days back to the user.
**Consent for Aadhaar**
I hereby voluntarily submit at my own discretion to VelocityPedia Private Limited, a copy of my Aadhaar card/ Aadhaar details, as issued by UIDAI, for the purpose of payment of my court challan. I hereby consent to VAAHANBUDDY to share my Aadhaar details with the third party service provider engaged by it solely for the purpose of making payment/settlement of court challan issued against my name by the traffic department. I hereby expressly declare that I have been informed by VAAHANBUDDY that: (a) My Aadhaar details will be used/shared for payment/settlement of court challans, and for no other purpose unless required under any law; and (b) my Aadhaar details/authentications records may be stored and maintained for the informed purpose above as well as for regulatory and legal reporting and filings or where required under any law; and (c) I may submit any other officially valid document in place of Aadhaar if permitted under applicable laws.
`;

  const handlePayment = async (challan) => {
    const challanId = challan?.document_id || challan?.id || challan?.challan_id;
    if (!agreedToTerms[challanId]) {
      alert('You must agree to the Terms and Conditions before proceeding with payment.');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Please log in to proceed with payment.');
        return;
      }

      const vehicleNumber = vehicleDetails?.document_id;
      if (!vehicleNumber) {
        alert('Vehicle number is required to proceed with payment.');
        return;
      }

      if (!challanId) {
        console.error('Challan ID not found in challan object. Available keys:', Object.keys(challan));
        alert('Challan ID is missing. Please try again.');
        return;
      }

      // Check if Razorpay script is loaded
      if (!window.Razorpay) {
        alert('Payment gateway is loading. Please wait a moment and try again.');
        return;
      }

      let formattedDate;
      if (challan.issueDate) {
        formattedDate = challan.issueDate;
      } else if (challan.date_issued) {
        const dateTimeRegex = /^(\d{2})\/(\d{2})\/(\d{4})(?:\s(\d{2}):(\d{2}):(\d{2}))?$/;
        const match = challan.date_issued.match(dateTimeRegex);
        if (match) {
          const [, day, month, year] = match;
          formattedDate = `${year}-${month}-${day}`;
        } else {
          formattedDate = new Date(challan.date_issued).toISOString().split('T')[0];
        }
      }

      const response = await fetch(`${API_BASE_URL}/api/vehicles/challans/${challanId}/create-order`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          vehicle_number: vehicleNumber,
          amount: Number(challan.amount),
          date_issued: formattedDate,
          offence: challan.offence,
          area: challan.area,
          state: challan.state,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.message === 'Challan already paid') {
          alert('This challan has already been paid.');
          return;
        }
        throw new Error(errorData.message || 'Failed to create payment order');
      }

      const { order_id, amount, currency, service_fee, challan_amount, total_amount } = await response.json();

      if (!order_id) {
        throw new Error('Invalid order received from server. Please try again.');
      }

      const options = {
        key: 'rzp_live_SxuxGiTXJQIkGM',
        amount: amount,
        currency: currency,
        name: 'VAAHANBUDDY',
        description: `Payment for challan ${challanId}`,
        image: 'https://api.rtoxpert.com/static/media/rtoxpertLogo.c75aa58572ed90aeba16.png',
        order_id: order_id,
        handler: async function (response) {
          try {
            const verifyResponse = await fetch(`${API_BASE_URL}/api/vehicles/challans/${challanId}/verify-payment`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature,
              }),
            });

            if (!verifyResponse.ok) {
              throw new Error('Payment verification failed');
            }

            const updatedChallan = { ...challan, status: 'Paid', service_fee, total_amount };
            setPaidChallanForModal(updatedChallan);
            setShowPaymentSuccessModal(true);
            setCurrentChallanId(challanId);
            setPendingChallans(pendingChallans.filter(c => (c.document_id || c.id || c.challan_id) !== challanId));
            setPaidChallans([...paidChallans, updatedChallan]);
          } catch (error) {
            console.error('Error verifying payment:', error);
            alert('Payment successful but failed to verify or update status. Please contact support.');
          }
        },
        prefill: {
          name: challan.accusedName || 'Unknown',
          email: '',
          contact: '',
        },
        theme: {
          color: '#3399cc',
        },
        modal: {
          ondismiss: function () {
            console.log('Razorpay checkout modal closed by user');
          },
        },
      };

      try {
        const rzp = new window.Razorpay(options);
        rzp.on('payment.failed', function (response) {
          alert('Payment failed. Please try again or contact support.');
          console.error('Payment failed:', response.error);
        });
        rzp.open();
      } catch (rzpError) {
        console.error('Razorpay checkout error:', rzpError);
        alert('Oops! Something went wrong.\nError in opening checkout.\nPlease refresh the page and try again.');
      }
    } catch (error) {
      console.error('Payment initiation error:', error);
      alert(`Failed to initiate payment: ${error.message}. Please try again.`);
    }
  };

  const handlePayAll = async () => {
    const selectedChallans = pendingChallans.filter(challan => agreedToTerms[challan.id]);
    if (selectedChallans.length === 0) {
      alert('Please select at least one challan to pay.');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Please log in to proceed with payment.');
        return;
      }

      const vehicleNumber = vehicleDetails?.document_id;
      if (!vehicleNumber) {
        alert('Vehicle number is required to proceed with payment.');
        return;
      }

      const selectedChallanData = selectedChallans.map(challan => ({
        challan_id: challan.document_id || challan.id,
        amount: Number(challan.amount),
        date_issued: challan.issueDate ? challan.issueDate : undefined,
        offence: challan.offence,
        area: challan.area,
        state: challan.state,
      }));

      const response = await fetch(`${API_BASE_URL}/api/vehicles/challans/create-bulk-order`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          vehicle_number: vehicleNumber,
          challans: selectedChallanData,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.message.includes('already paid')) {
          alert('One or more selected challans have already been paid.');
          return;
        }
        throw new Error(errorData.message || 'Failed to create bulk payment order');
      }

      const { order_id, amount, currency, breakdown } = await response.json();

      const options = {
        key: 'rzp_live_SxuxGiTXJQIkGM',
        amount: amount,
        currency: currency,
        name: 'VAAHANBUDDY',
        description: `Bulk payment for selected challans`,
        image: 'https://api.rtoxpert.com/static/media/rtoxpertLogo.c75aa58572ed90aeba16.png',
        order_id: order_id,
        handler: async function (response) {
          try {
            const selectedChallanIds = selectedChallans.map(c => c.id);
            const verifyResponse = await fetch(`${API_BASE_URL}/api/vehicles/challans/verify-payment`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature,
                challan_ids: selectedChallanIds,
              }),
            });

            if (!verifyResponse.ok) {
              const errorData = await verifyResponse.json();
              throw new Error(errorData.message || 'Bulk payment verification failed');
            }

            const updatedChallans = selectedChallans.map(challan => {
              const challanBreakdown = breakdown.find(b => b.challan_id === (challan.document_id || challan.id));
              return {
                ...challan,
                status: 'Paid',
                service_fee: challanBreakdown.service_fee,
                total_amount: challanBreakdown.total_amount,
              };
            });

            setPaidChallanForModal(updatedChallans);
            setShowPaymentSuccessModal(true);
            setPendingChallans(pendingChallans.filter(c => !selectedChallanIds.includes(c.id)));
            setPaidChallans([...paidChallans, ...updatedChallans]);
            setAgreedToTerms(prev => {
              const newAgreed = { ...prev };
              selectedChallanIds.forEach(id => delete newAgreed[id]);
              return newAgreed;
            });
          } catch (error) {
            console.error('Error verifying bulk payment:', error);
            alert(`Payment successful but failed to verify or update status: ${error.message}. Please contact support.`);
          }
        },
        prefill: {
          name: selectedChallans[0]?.accusedName || 'Unknown',
          email: '',
          contact: '',
        },
        theme: {
          color: '#3399cc',
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', function (response) {
        alert('Payment failed. Please try again or contact support.');
        console.error('Payment failed:', response.error);
      });
      rzp.open();
    } catch (error) {
      console.error('Bulk payment initiation error:', error);
      alert(`Failed to initiate bulk payment: ${error.message}. Please try again.`);
    }
  };

  const PaymentSuccessModal = ({ challans }) => {
    if (!showPaymentSuccessModal || !challans) return null;

    const isBulkPayment = Array.isArray(challans);
    const totalChallanAmount = isBulkPayment
      ? challans.reduce((sum, c) => sum + c.amount, 0)
      : challans.amount;
    const totalServiceFee = isBulkPayment
      ? challans.reduce((sum, c) => sum + (c.service_fee || 0), 0)
      : challans.service_fee || 0;
    const totalAmount = isBulkPayment
      ? challans.reduce((sum, c) => sum + (c.total_amount || c.amount), 0)
      : challans.total_amount || challans.amount;

    return (
      <div className={styles.modalOverlay} onClick={() => {
        setShowPaymentSuccessModal(false);
        setPaidChallanForModal(null);
      }}>
        <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
          <div className={styles.modalHeader}>
            <h2>Payment Successful</h2>
            <button className={styles.closeBtn} onClick={() => {
              setShowPaymentSuccessModal(false);
              setPaidChallanForModal(null);
            }}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          <div className={styles.modalBody}>
            <p>Your {isBulkPayment ? 'challans have' : 'challan has'} been successfully processed.</p>
            {isBulkPayment ? (
              <>
                {challans.map((challan, index) => (
                  <div key={index} style={{ marginBottom: '15px' }}>
                    <p><strong>Challan {challan.id}:</strong></p>
                    <p>Challan Amount: ₹{challan.amount.toLocaleString()}</p>
                    <p>Service Fee: ₹{challan.service_fee ? challan.service_fee.toLocaleString() : 'N/A'}</p>
                    <p>Total: ₹{challan.total_amount ? challan.total_amount.toLocaleString() : challan.amount.toLocaleString()}</p>
                  </div>
                ))}
                <p><strong>Total Challan Amount:</strong> ₹{totalChallanAmount.toLocaleString()}</p>
                <p><strong>Total Service Fee:</strong> ₹{totalServiceFee.toLocaleString()}</p>
                <p><strong>Grand Total:</strong> ₹{totalAmount.toLocaleString()}</p>
              </>
            ) : (
              <>
                <p><strong>Challan Amount:</strong> ₹{challans.amount.toLocaleString()}</p>
                <p><strong>Service Fee:</strong> ₹{challans.service_fee ? challans.service_fee.toLocaleString() : 'N/A'}</p>
                <p><strong>Total Amount:</strong> ₹{challans.total_amount ? challans.total_amount.toLocaleString() : challans.amount.toLocaleString()}</p>
              </>
            )}
            <p><strong>The challan(s) will be settled in 3 working days.</strong></p>
            <button
              className={styles.actionBtn}
              style={{ marginTop: '20px', width: '100%' }}
              onClick={() => {
                setShowPaymentSuccessModal(false);
                setPaidChallanForModal(null);
              }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  const PayAllModal = () => {
    if (!showPayAllModal) return null;

    const selectedChallans = pendingChallans.filter(challan => agreedToTerms[challan.id]);
    const totalChallanAmount = selectedChallans.reduce((sum, c) => sum + c.amount, 0);
    const totalServiceFee = selectedChallans.reduce((sum, c) => sum + c.service_fee, 0);
    const totalAmount = selectedChallans.reduce((sum, c) => sum + c.total_amount, 0);

    return (
      <div className={styles.modalOverlay} onClick={() => setShowPayAllModal(false)}>
        <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
          <div className={styles.modalHeader}>
            <h2>Pay Selected Challans</h2>
            <button className={styles.closeBtn} onClick={() => setShowPayAllModal(false)}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          <div className={styles.modalBody}>
            <p>Select the challans you want to pay:</p>
            {pendingChallans.map((challan, index) => (
              <div key={index} style={{ marginBottom: '15px' }}>
                <label>
                  <input
                    type="checkbox"
                    checked={agreedToTerms[challan.id]}
                    onChange={(e) => setAgreedToTerms({ ...agreedToTerms, [challan.id]: e.target.checked })}
                  />
                  Challan {challan.id}: ₹{challan.amount.toLocaleString()} + Service Fee: ₹{challan.service_fee.toLocaleString()} = Total: ₹{challan.total_amount.toLocaleString()}
                </label>
              </div>
            ))}
            <p><strong>Total Challan Amount:</strong> ₹{totalChallanAmount.toLocaleString()}</p>
            <p><strong>Total Service Fee:</strong> ₹{totalServiceFee.toLocaleString()}</p>
            <p><strong>Grand Total:</strong> ₹{totalAmount.toLocaleString()}</p>
            <button
              className={styles.payBtn}
              style={{ marginTop: '20px', width: '100%' }}
              onClick={() => {
                setShowPayAllModal(false);
                handlePayAll();
              }}
              disabled={selectedChallans.length === 0}
            >
              Confirm Payment
            </button>
            <button
              className={styles.actionBtn}
              style={{ marginTop: '10px', width: '100%' }}
              onClick={() => setShowPayAllModal(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  };

  const TermsModal = () => {
    if (!showTermsModal) return null;

    return (
      <div className={styles.modalOverlay} onClick={() => setShowTermsModal(false)}>
        <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
          <div className={styles.modalHeader}>
            <h2>Terms and Conditions</h2>
            <button className={styles.closeBtn} onClick={() => setShowTermsModal(false)}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          <div className={styles.modalBody}>
            <div style={{ maxHeight: '400px', overflowY: 'auto', whiteSpace: 'pre-wrap' }}>
              {termsAndConditions}
            </div>
            <button
              className={styles.actionBtn}
              style={{ marginTop: '20px', width: '100%' }}
              onClick={() => setShowTermsModal(false)}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  // FIXED: Simplified challan classification based on court_challan field
  const classifyChallans = (challanData) => {
    return challanData.map(challan => {
      // Use court_status field from API — "No" means e-challan, anything starting with "Yes" is a court challan
      const courtStatus = (challan.court_status || '').toString().trim().toLowerCase();
      const type = courtStatus.startsWith('yes') ? 'COURT-CHALLAN' : 'E-CHALLAN';

      return {
        ...challan,
        challan_type: type
      };
    });
  };

  const maskAlternateCharacters = (text) => {
    if (!text) return '';
    return text.split('').map((char, index) => (index % 2 === 1 ? '*' : char)).join('');
  };

  const maskFullString = (text) => {
    if (!text) return '';
    return '*'.repeat(text.length);
  };

  const maskLastFiveCharacters = (text) => {
    if (!text) return '';
    if (text.length <= 5) return '*'.repeat(text.length);
    return text.slice(0, -5) + '*'.repeat(5);
  };

  // FIXED: Updated vehicle image detection for car
  const getVehicleImage = (vehicleDetails) => {
    if (!vehicleDetails || !vehicleDetails.vehicle_data) return bikeImage;

    const categoryDescription = vehicleDetails.vehicle_data.category_description?.toLowerCase() || '';
    const bodyType = vehicleDetails.vehicle_data.body_type?.toLowerCase() || '';
    const makerDescription = vehicleDetails.vehicle_data.maker_description?.toLowerCase() || '';

    // Check for cars first (more specific matching)
    if (
      categoryDescription.includes('motor car') ||
      categoryDescription.includes('passenger') ||
      categoryDescription.includes('car') ||
      bodyType.includes('saloon') ||
      bodyType.includes('hatchback') ||
      bodyType.includes('sedan') ||
      bodyType.includes('suv') ||
      categoryDescription.includes('light motor vehicle') ||
      categoryDescription.includes('lmv')
    ) {
      return carImage;
    }

    // Check for trucks/commercial vehicles
    if (
      categoryDescription.includes('goods') ||
      categoryDescription.includes('truck') ||
      categoryDescription.includes('commercial') ||
      categoryDescription.includes('heavy') ||
      bodyType.includes('truck') ||
      bodyType.includes('goods') ||
      bodyType.includes('delivery')
    ) {
      return truckImage;
    }

    // Check for motorcycles/scooters (bikes)
    if (
      categoryDescription.includes('m-cycle') ||
      categoryDescription.includes('scooter') ||
      categoryDescription.includes('motorcycle') ||
      categoryDescription.includes('bike') ||
      bodyType.includes('two wheeler') ||
      bodyType.includes('motor cycle') ||
      bodyType.includes('scooter')
    ) {
      // If it's specifically a scooter
      if (
        categoryDescription.includes('scooter') ||
        makerDescription.includes('access') ||
        makerDescription.includes('activa') ||
        makerDescription.includes('jupiter') ||
        makerDescription.includes('fascino')
      ) {
        return scooterImage;
      }
      return bikeImage;
    }

    // Default to car for any unspecified vehicle (since most vehicles are cars)
    return carImage;
  };

  const getVehicleTypeName = (vehicleDetails) => {
    if (!vehicleDetails || !vehicleDetails.vehicle_data) return 'Vehicle';

    const categoryDescription = vehicleDetails.vehicle_data.category_description?.toLowerCase() || '';
    const bodyType = vehicleDetails.vehicle_data.body_type?.toLowerCase() || '';

    if (categoryDescription.includes('motor car') || categoryDescription.includes('passenger') || categoryDescription.includes('lmv')) return 'Car';

    if (categoryDescription.includes('goods') || categoryDescription.includes('truck')) return 'Truck';

    if (
      categoryDescription.includes('m-cycle') ||
      categoryDescription.includes('scooter') ||
      bodyType.includes('two wheeler')
    ) {
      if (categoryDescription.includes('scooter')) return 'Scooter';
      return 'Motorcycle';
    }

    return 'Car'; // Default to Car
  };

  const generateRcPdf = () => {
    const doc = new jsPDF();
    let yOffset = 10;

    const addSection = (title, fields, startY) => {
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(title, 10, startY);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      let y = startY + 10;

      Object.entries(fields).forEach(([label, value]) => {
        if (value) {
          doc.text(`${label}: ${value}`, 10, y);
          y += 7;
        }
      });

      return y;
    };

    const ownershipFields = {
      'Owner Name': vehicleDetails?.owner_data?.name || 'N/A',
      "Father's Name": vehicleDetails?.owner_data?.father_name || 'N/A',
      'Present Address': vehicleDetails?.owner_data?.present_address || 'N/A',
      'Permanent Address': vehicleDetails?.owner_data?.permanent_address || 'N/A',
      'Registered RTO': vehicleDetails?.registered_at || 'N/A',
      Financed: vehicleDetails?.financed ? 'Yes' : 'No',
      Carrier: vehicleDetails?.is_commercial ? 'Commercial' : 'Non-Commercial',
    };
    yOffset = addSection('Ownership Details', ownershipFields, yOffset);

    yOffset += 10;
    const vehicleFields = {
      'Vehicle Make': vehicleDetails?.vehicle_data?.maker_description || 'N/A',
      'Vehicle Model': vehicleDetails?.vehicle_data?.maker_model || 'N/A',
      'Chassis Number': vehicleDetails?.vehicle_data?.chassis_number || 'N/A',
      'Engine Number': vehicleDetails?.vehicle_data?.engine_number || 'N/A',
      'Fuel Type': vehicleDetails?.vehicle_data?.fuel_type || 'N/A',
      'Body Type': vehicleDetails?.vehicle_data?.body_type || 'N/A',
      Color: vehicleDetails?.vehicle_data?.color || 'N/A',
    };
    yOffset = addSection('Vehicle Details', vehicleFields, yOffset);

    yOffset += 10;
    const importantFields = {
      'Registration Number': vehicleDetails?.document_id || 'N/A',
      'Registration Date': vehicleDetails?.issue_date || 'N/A',
      'Fitness Valid Upto': vehicleDetails?.expiry_date || 'N/A',
      'Insurance Policy Number': vehicleDetails?.insurance_data?.policy_number || 'N/A',
      'Insurance Company': vehicleDetails?.insurance_data?.company || 'N/A',
      'Insurance Expiry Date': vehicleDetails?.insurance_data?.expiry_date || 'N/A',
      'PUC Number': vehicleDetails?.pucc_data?.pucc_number || 'N/A',
      'PUC Expiry Date': vehicleDetails?.pucc_data?.expiry_date || 'N/A',
      'Tax Validity': vehicleDetails?.tax_end_date || 'N/A',
    };
    yOffset = addSection('Important Details', importantFields, yOffset);

    yOffset += 10;
    const otherFields = {
      'Vehicle Category': vehicleDetails?.vehicle_data?.category_description || 'N/A',
      'Cubic Capacity': vehicleDetails?.vehicle_data?.cubic_capacity ? `${vehicleDetails.vehicle_data.cubic_capacity} CC` : 'N/A',
      'Gross Weight': vehicleDetails?.vehicle_data?.gross_weight ? `${vehicleDetails.vehicle_data.gross_weight} kg` : 'N/A',
      'Unladen Weight': vehicleDetails?.vehicle_data?.unladen_weight ? `${vehicleDetails.vehicle_data.unladen_weight} kg` : 'N/A',
      'Number of Cylinders': vehicleDetails?.vehicle_data?.number_of_cylinders || 'N/A',
      Wheelbase: vehicleDetails?.vehicle_data?.wheelbase ? `${vehicleDetails.vehicle_data.wheelbase} mm` : 'N/A',
      'Norms Type': vehicleDetails?.norms_type || 'N/A',
      'Commercial Vehicle': vehicleDetails?.is_commercial ? 'Yes' : 'No',
      'Blacklist Status': vehicleDetails?.blacklist_status === 'false' ? 'Not Blacklisted' : 'Blacklisted',
    };
    yOffset = addSection('Other Info', otherFields, yOffset);

    return doc.output('blob');
  };

  // Step 1: POST /verify-rc to get request_id
  const startVerification = async (rcNumber) => {
    const token = localStorage.getItem("token");
    const headers = new Headers({ "Content-Type": "application/json" });
    if (token) headers.append("Authorization", `Bearer ${token}`);

    const response = await fetch(`${API_BASE_URL}/api/rc/verify-rc`, {
      method: "POST",
      headers,
      body: JSON.stringify({ rc_number: rcNumber }),
    });
    if (!response.ok) throw new Error("Failed to start verification");

    const data = await response.json();
    if (!data.request_id) throw new Error("Missing request_id from verification API");
    return data.request_id;
  };

  // Step 2: Poll GET /verification-status/:request_id until done
  const pollVerificationResult = async (requestId, maxTries = 10, intervalMs = 3000) => {
    const token = localStorage.getItem("token");
    const headers = new Headers({ "Content-Type": "application/json" });
    if (token) headers.append("Authorization", `Bearer ${token}`);

    for (let i = 0; i < maxTries; i++) {
      const response = await fetch(`${API_BASE_URL}/api/rc/verification-status/${requestId}`, {
        method: "GET",
        headers,
      });
      if (!response.ok) throw new Error("Failed to fetch verification status");

      const data = await response.json();

      if (data.status === 200 && data.data?.rc_data) {
        return data.data.rc_data;
      } else if (data.status === 400 || data.status === 404) {
        throw new Error(data.message || "Verification failed or not found");
      }
      // status 202 = still processing
      await new Promise(resolve => setTimeout(resolve, intervalMs));
    }
    throw new Error("Verification timed out");
  };

  // Combined fetchVehicleDetails using above steps
  const fetchVehicleDetails = async (rcNumber) => {
    try {
      const requestId = await startVerification(rcNumber);
      const rcResult = await pollVerificationResult(requestId);
      return {
        status: 200,
        data: {
          code: "1000",
          rc_data: rcResult
        }
      };
    } catch (err) {
      console.error('Error fetching vehicle details:', err);
      throw err;
    }
  };

  // Updated fetchChallanDetails for frontend
  const fetchChallanDetails = async (rcNumber, chassisNumber) => {
    try {
      const token = localStorage.getItem("token");
      const headers = new Headers({ "Content-Type": "application/json" });
      if (token) headers.append("Authorization", `Bearer ${token}`);

      const requestBody = {
        rc_number: rcNumber,
        chassis_number: chassisNumber.slice(-5)
      };

      const response = await fetch(`${API_BASE_URL}/api/challan/verify-challan`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(requestBody),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(`Failed to fetch challan details: ${result.message || response.statusText}`);
      }

      // Now you get the complete response with challan data
      return {
        status: 200,
        data: result.result.source_output
      };
    } catch (error) {
      console.error('Error fetching challan details:', error);
      // Return empty challan data if API fails
      return {
        status: 200,
        data: {
          rc_number: rcNumber,
          challan_summary: {
            total_challan_number: "0",
            total_pending_challan: "0",
            total_paid_challan: "0"
          },
          challan_details: [],
          status: "id_not_found"
        }
      };
    }
  };

  useEffect(() => {

    if (challanData) {
      // Handle new API response format
      if (challanData.challan_details && Array.isArray(challanData.challan_details)) {

        const classifiedChallans = classifyChallans(challanData.challan_details);
        const pending = [];
        const paid = [];

        const getFormattedDate = (dateString) => {
          if (!dateString) return 'N/A';
          return dateString;
        };

        classifiedChallans.forEach((challan) => {
          const formattedIssueDate = getFormattedDate(challan.date_of_challan);

          // Check for pending/unpaid challans
          if (challan.challan_status === 'pending' || challan.challan_status === 'unpaid') {
            const service_fee = challan.amount <= 1000 ? 152.22 : 187.62;
            const total_amount = challan.amount + service_fee;
            pending.push({
              id: challan.challan_number,
              issueDate: formattedIssueDate,
              amount: challan.amount,
              service_fee,
              total_amount,
              offence: challan.offense_details || 'Traffic violation',
              area: challan.challan_place,
              state: challan.state,
              accusedName: challan.accused_name,
              challan_type: challan.challan_type,
              court_status: challan.court_status || '', // Add court status
            });
          }
          // Check for paid challans
          else if (challan.challan_status === 'disposed' || challan.challan_status === 'cash' || challan.challan_status === 'paid') {
            paid.push({
              id: challan.challan_number,
              issueDate: formattedIssueDate,
              amount: challan.amount,
              service_fee: challan.service_fee || 0,
              total_amount: challan.total_amount || challan.amount,
              offence: challan.offense_details || 'Traffic violation',
              area: challan.challan_place,
              state: challan.state,
              accusedName: challan.accused_name,
              challan_type: challan.challan_type,
              court_status: challan.court_status || '', // Add court status
            });
          }
        });

        setPendingChallans(pending);
        setPaidChallans(paid);
        setAgreedToTerms(pending.reduce((acc, challan) => ({ ...acc, [challan.id]: false }), {}));
      } else if (challanData.status === "id_not_found") {
        setPendingChallans([]);
        setPaidChallans([]);
        setAgreedToTerms({});
      }
    }
  }, [challanData]);

  const validateVehicleNumber = (number) => {
    const cleanedNumber = number.replace(/\s/g, '').toUpperCase();
    const regex = /^[A-Z]{2}[0-9]{1,2}[A-Z]{0,3}[0-9]{1,4}$/;
    const validStateCodes = [
      'AN', 'AP', 'AR', 'AS', 'BR', 'CH', 'CG', 'DD', 'DL', 'GA', 'GJ', 'HR', 'HP',
      'JH', 'JK', 'KA', 'KL', 'LD', 'MP', 'MH', 'MN', 'ML', 'MZ', 'NL', 'OD',
      'PY', 'PB', 'RJ', 'SK', 'TN', 'TS', 'TR', 'UP', 'UK', 'WB'
    ];

    if (!cleanedNumber) return 'Please enter a vehicle number';
    if (!regex.test(cleanedNumber)) return 'Invalid vehicle number format.';
    if (!validStateCodes.includes(cleanedNumber.substring(0, 2))) return 'Invalid state code.';
    return '';
  };

  const addVehicleToGarage = async () => {
    if (!vehicleDetails) {
      setError('No vehicle details available to add to garage.');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setError('Authentication token missing. Please log in again.');
      return;
    }

    setAddingToGarage(true);
    setError('');

    try {
      const myHeaders = new Headers();
      myHeaders.append('Authorization', `Bearer ${token}`);

      const formdata = new FormData();
      formdata.append('vehicle_number', vehicleDetails.document_id);

      const vehicleType =
        vehicleDetails.vehicle_data.category_description?.toLowerCase().includes('motor cycle') ||
          vehicleDetails.vehicle_data.body_type?.toLowerCase().includes('motor cycle') ||
          vehicleDetails.vehicle_data.body_type?.toLowerCase().includes('scooter')
          ? 'bike'
          : 'car';

      formdata.append('vehicle_type', vehicleType);
      formdata.append('brand', vehicleDetails.vehicle_data.maker_description || 'Unknown');
      formdata.append('model', vehicleDetails.vehicle_data.maker_model || 'Unknown');

      const issueYear = vehicleDetails.issue_date
        ? new Date(vehicleDetails.issue_date).getFullYear()
        : new Date().getFullYear();
      formdata.append('year_of_registration', issueYear.toString());

      formdata.append('fuel_type', vehicleDetails.vehicle_data.fuel_type?.toLowerCase() || 'petrol');
      formdata.append('chassis_number', vehicleDetails.vehicle_data.chassis_number || '');

      const rcPdf = generateRcPdf();
      formdata.append('documents', rcPdf, `RC_${vehicleDetails.document_id}.pdf`);
      formdata.append('document_types', 'RC');

      const expiryDate = vehicleDetails.expiry_date
        ? new Date(vehicleDetails.expiry_date).toISOString().split('T')[0]
        : new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      formdata.append('expiry_dates', expiryDate);

      const dummyImage = new Blob(['Vehicle Image'], { type: 'image/png' });
      formdata.append('vehicle_image', dummyImage, `vehicle_${vehicleDetails.document_id}.png`);

      const requestOptions = {
        method: 'POST',
        headers: myHeaders,
        body: formdata,
        redirect: 'follow',
      };

      const response = await fetch(`${API_BASE_URL}/api/vehicles`, requestOptions);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to add vehicle: ${errorText}`);
      }

      const result = await response.text();
      console.log('Vehicle added successfully:', result);

      navigate('/garage');
    } catch (error) {
      console.error('Error adding vehicle to garage:', error);
      setError(`Failed to add vehicle to garage: ${error.message}`);
    } finally {
      setAddingToGarage(false);
    }
  };

  const handleSearch = async () => {
    const validationError = validateVehicleNumber(vehicleNumber);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError('');
    setLoading(true);
    const cleanedVehicleNumber = vehicleNumber.replace(/\s+/g, '').toUpperCase();

    try {
      navigate('/search');
      const vehicleDetailsResponse = await fetchVehicleDetails(cleanedVehicleNumber);

      if (vehicleDetailsResponse.status === 200 && vehicleDetailsResponse.data.code === '1000') {
        const { chassis_number } = vehicleDetailsResponse.data.rc_data.vehicle_data;
        const challanDetailsResponse = await fetchChallanDetails(cleanedVehicleNumber, chassis_number);

        setVehicleDetails(vehicleDetailsResponse.data.rc_data);
        setChallanData(challanDetailsResponse.data);

        setTimeout(() => {
          navigate('/challandetails', {
            state: {
              vehicleNumber: cleanedVehicleNumber,
              vehicleDetails: vehicleDetailsResponse.data.rc_data,
              challanDetails: challanDetailsResponse.data,
            },
          });
          setLoading(false);
        }, 2000);
      } else {
        setError('Vehicle details not found.');
        setLoading(false);
        setTimeout(() => navigate(-1), 1000);
      }
    } catch (error) {
      setError('Failed to fetch vehicle details.');
      setLoading(false);
      setTimeout(() => navigate(-1), 1000);
    }
  };

  // Toggle individual challan selection
  const toggleChallanSelection = (challanId) => {
    setSelectedChallans(prev => ({
      ...prev,
      [challanId]: !prev[challanId]
    }));
    // Also set agreed to terms when selected
    setAgreedToTerms(prev => ({
      ...prev,
      [challanId]: !prev[challanId]
    }));
  };

  // Select all pending challans
  const handleSelectAll = () => {
    const allSelected = pendingChallans.every(c => selectedChallans[c.id]);
    if (allSelected) {
      // Deselect all
      const newSelected = {};
      const newAgreed = {};
      pendingChallans.forEach(c => {
        newSelected[c.id] = false;
        newAgreed[c.id] = false;
      });
      setSelectedChallans(newSelected);
      setAgreedToTerms(newAgreed);
    } else {
      // Select all
      const newSelected = {};
      const newAgreed = {};
      pendingChallans.forEach(c => {
        newSelected[c.id] = true;
        newAgreed[c.id] = true;
      });
      setSelectedChallans(newSelected);
      setAgreedToTerms(newAgreed);
    }
  };

  // Get selected count
  const getSelectedCount = () => {
    return Object.values(selectedChallans).filter(Boolean).length;
  };

  // Get total selected amount
  const getSelectedTotal = () => {
    return pendingChallans
      .filter(c => selectedChallans[c.id])
      .reduce((sum, c) => sum + c.total_amount, 0);
  };

  // Separate court challans from e-challans
  const getEChallans = () => pendingChallans.filter(c => c.challan_type === 'E-CHALLAN');
  const getCourtChallans = () => pendingChallans.filter(c => c.challan_type === 'COURT-CHALLAN');

  // Robust date parser
  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    if (dateStr instanceof Date) return dateStr;
    const str = String(dateStr).trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
      const date = new Date(str);
      if (!isNaN(date.getTime())) return date;
    }
    const ddmmyyyy = str.match(/^(\d{1,2})[-\/](\d{1,2})[-\/](\d{4})$/);
    if (ddmmyyyy) {
      const [, day, month, year] = ddmmyyyy;
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    }
    const date = new Date(str);
    if (!isNaN(date.getTime())) return date;
    return null;
  };

  // Helper to safely access nested properties with fallback keys
  const getRCValue = (data, paths, defaultValue = 'N/A') => {
    if (!data) return defaultValue;
    for (const path of paths) {
      const keys = path.split('.');
      let value = data;
      for (const key of keys) {
        value = value?.[key];
        if (value === undefined || value === null) break;
      }
      if (value !== undefined && value !== null && value !== '') return value;
    }
    return defaultValue;
  };

  const formatVehicleNumber = (number) => {
    if (!number) return '';
    const cleanNumber = number.replace(/\s/g, '');
    return cleanNumber.replace(/^([A-Z]{2})([0-9]{1,2})([A-Z]{0,3})([0-9]{1,4})$/, '$1 $2 $3 $4');
  };

  const formatDate = (dateString) => {
    const date = parseDate(dateString);
    if (!date) return 'N/A';
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const RCDetailsModal = () => {
    if (!showRCModal || !vehicleDetails) return null;

    return (
      <div className={styles.modalOverlay} onClick={() => setShowRCModal(false)}>
        <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
          <div className={styles.modalHeader}>
            <h2>RC Details</h2>
            <button className={styles.closeBtn} onClick={() => setShowRCModal(false)}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          <div className={styles.modalBody}>
            <div className={styles.detailSection}>
              <h3>Owner Information</h3>
              <div className={styles.detailRow}>
                <span className={styles.label}>Serial:</span>
                <span className={styles.value}>{getRCValue(vehicleDetails, ['owner_data.serial', 'rc_owner_sr_no'])}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.label}>Name:</span>
                <span className={styles.value}>{maskAlternateCharacters(getRCValue(vehicleDetails, ['owner_data.name', 'owner_name', 'rc_owner_name']))}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.label}>Father's Name:</span>
                <span className={styles.value}>{maskAlternateCharacters(getRCValue(vehicleDetails, ['owner_data.father_name', 'father_name', 'rc_f_name']))}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.label}>Present Address:</span>
                <span className={styles.value}>{maskAlternateCharacters(getRCValue(vehicleDetails, ['owner_data.present_address', 'present_address', 'rc_present_address']))}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.label}>Permanent Address:</span>
                <span className={styles.value}>{maskAlternateCharacters(getRCValue(vehicleDetails, ['owner_data.permanent_address', 'permanent_address', 'rc_permanent_address']))}</span>
              </div>
            </div>
            <div className={styles.detailSection}>
              <h3>Vehicle Information</h3>
              <div className={styles.detailRow}>
                <span className={styles.label}>RC Number:</span>
                <span className={styles.value}>{getRCValue(vehicleDetails, ['registration_number', 'document_id', 'vehicle_number', 'rc_regn_no'])}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.label}>Maker:</span>
                <span className={styles.value}>{getRCValue(vehicleDetails, ['manufacturer', 'vehicle_data.maker_description', 'maker_description', 'rc_maker_desc'])}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.label}>Model:</span>
                <span className={styles.value}>{getRCValue(vehicleDetails, ['vehicle_data.maker_model', 'maker_model', 'rc_maker_model'])}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.label}>Body Type:</span>
                <span className={styles.value}>{getRCValue(vehicleDetails, ['vehicle_data.body_type', 'body_type', 'rc_body_type_desc'])}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.label}>Chassis Number:</span>
                <span className={styles.value}>{maskLastFiveCharacters(getRCValue(vehicleDetails, ['vehicle_data.chassis_number', 'chassis_number', 'rc_chasi_no']))}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.label}>Engine Number:</span>
                <span className={styles.value}>{maskLastFiveCharacters(getRCValue(vehicleDetails, ['vehicle_data.engine_number', 'engine_number', 'rc_eng_no']))}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.label}>Color:</span>
                <span className={styles.value}>{getRCValue(vehicleDetails, ['vehicle_data.color', 'color', 'rc_color'])}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.label}>Fuel Type:</span>
                <span className={styles.value}>{getRCValue(vehicleDetails, ['fuel_type', 'vehicle_data.fuel_type', 'fuel_desc', 'rc_fuel_desc'])}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.label}>Category:</span>
                <span className={styles.value}>{getRCValue(vehicleDetails, ['vehicle_class', 'vehicle_data.category_description', 'category_description', 'rc_vch_catg'])}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.label}>Cubic Capacity:</span>
                <span className={styles.value}>{getRCValue(vehicleDetails, ['cubic_capacity', 'vehicle_data.cubic_capacity', 'rc_cubic_cap'])}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.label}>Gross Weight:</span>
                <span className={styles.value}>{getRCValue(vehicleDetails, ['gross_weight', 'avg_gross_vehicle_weight', 'vehicle_data.gross_weight', 'rc_gvw'])}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.label}>Number of Cylinders:</span>
                <span className={styles.value}>{getRCValue(vehicleDetails, ['number_of_cylinders', 'vehicle_data.number_of_cylinders', 'rc_no_cyl'])}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.label}>Wheelbase:</span>
                <span className={styles.value}>{getRCValue(vehicleDetails, ['wheelbase', 'vehicle_data.wheelbase', 'rc_wheelbase'])}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.label}>Unladen Weight:</span>
                <span className={styles.value}>{getRCValue(vehicleDetails, ['unladen_weight', 'vehicle_data.unladen_weight', 'rc_unld_wt'])}</span>
              </div>
            </div>
            <div className={styles.detailSection}>
              <h3>Registration Information</h3>
              <div className={styles.detailRow}>
                <span className={styles.label}>Registered At:</span>
                <span className={styles.value}>{getRCValue(vehicleDetails, ['rto_name', 'registered_at', 'rto', 'rc_rto_office_code'])}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.label}>Registration Date:</span>
                <span className={styles.value}>{formatDate(getRCValue(vehicleDetails, ['issue_date', 'registration_date', 'rc_regn_dt']))}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.label}>Fitness Valid upto:</span>
                <span className={styles.value}>{formatDate(getRCValue(vehicleDetails, ['fitness_upto', 'expiry_date', 'fitness_expiry', 'rc_fit_upto']))}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.label}>Tax End Date:</span>
                <span className={styles.value}>{formatDate(getRCValue(vehicleDetails, ['tax_end_date', 'tax_expiry', 'rc_tax_upto']))}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.label}>Blacklist Status:</span>
                <span className={styles.value}>{(getRCValue(vehicleDetails, ['blacklist_status', 'rc_blacklist_status']) === 'true' || getRCValue(vehicleDetails, ['blacklist_status', 'rc_blacklist_status']) === true) ? 'Blacklisted' : 'Not Blacklisted'}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.label}>Norms Type:</span>
                <span className={styles.value}>{getRCValue(vehicleDetails, ['norms_type', 'rc_norms_desc'])}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.label}>Financed:</span>
                <span className={styles.value}>{vehicleDetails.financed ? 'Yes' : 'No'}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.label}>Is Commercial:</span>
                <span className={styles.value}>{vehicleDetails.is_commercial ? 'Yes' : 'No'}</span>
              </div>
            </div>
            {vehicleDetails.insurance_data && (
              <div className={styles.detailSection}>
                <h3>Insurance Information</h3>
                <div className={styles.detailRow}>
                  <span className={styles.label}>Policy Number:</span>
                  <span className={styles.value}>{maskFullString(vehicleDetails.insurance_data.policy_number)}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.label}>Company:</span>
                  <span className={styles.value}>{vehicleDetails.insurance_data.company}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.label}>Expiry Date:</span>
                  <span className={styles.value}>{formatDate(vehicleDetails.insurance_data.expiry_date)}</span>
                </div>
              </div>
            )}
            {vehicleDetails.pucc_data && (
              <div className={styles.detailSection}>
                <h3>PUCC Information</h3>
                <div className={styles.detailRow}>
                  <span className={styles.label}>PUCC Number:</span>
                  <span className={styles.value}>{maskLastFiveCharacters(vehicleDetails.pucc_data.pucc_number)}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.label}>Expiry Date:</span>
                  <span className={styles.value}>{formatDate(vehicleDetails.pucc_data.expiry_date)}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.mainContainer}>
      {/* Header */}
      <div className={styles.headerSection}>
        <div className={styles.headerLeft}>
          <button className={styles.backButton} onClick={() => navigate(-1)}>
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          <span className={styles.vehicleNumberHeader}>
            {formatVehicleNumber(getRCValue(vehicleDetails, ['registration_number', 'document_id', 'vehicle_number', 'rc_regn_no'], vehicleNumber))}
          </span>
        </div>
        <div className={styles.headerActions}>
          <button className={styles.headerIconBtn} onClick={() => window.location.reload()}>
            <FontAwesomeIcon icon={faSync} />
          </button>
          <button className={styles.headerIconBtn}>
            <FontAwesomeIcon icon={faShare} />
          </button>
        </div>
      </div>

      {/* Tab Bar */}
      <div className={styles.tabBar}>
        <button
          className={`${styles.tabButton} ${activeTab === 'pending' ? styles.active : styles.inactive}`}
          onClick={() => setActiveTab('pending')}
        >
          <FontAwesomeIcon icon={faClock} />
          Pending
          <span className={styles.tabBadge}>{pendingChallans.length}</span>
        </button>
        <button
          className={`${styles.tabButton} ${activeTab === 'paid' ? styles.active : styles.inactive}`}
          onClick={() => setActiveTab('paid')}
        >
          <FontAwesomeIcon icon={faCheck} />
          Paid
          <span className={styles.tabBadge}>{paidChallans.length}</span>
        </button>
      </div>

      {/* Content Area */}
      <div className={styles.contentArea}>
        {activeTab === 'pending' ? (
          <>
            {/* Pending eChallans Section */}
            {getEChallans().length > 0 && (
              <>
                <div className={styles.sectionHeaderRow}>
                  <div className={styles.sectionTitleGroup}>
                    <div className={styles.sectionIcon}>
                      <FontAwesomeIcon icon={faFileAlt} />
                    </div>
                    <div className={styles.sectionTitleText}>
                      <h3 className={styles.sectionTitle}>Pending eChallan ({getEChallans().length})</h3>
                      <p className={styles.sectionSubtitle}>Updated Today</p>
                    </div>
                  </div>
                  <button
                    className={`${styles.selectAllBtn} ${pendingChallans.every(c => selectedChallans[c.id]) ? styles.selected : ''}`}
                    onClick={handleSelectAll}
                  >
                    <FontAwesomeIcon icon={faCheck} />
                    Select All
                  </button>
                </div>

                {getEChallans().map((challan, index) => (
                  <div
                    key={challan.id || index}
                    className={`${styles.challanCard} ${selectedChallans[challan.id] ? styles.selected : ''}`}
                  >
                    <div className={styles.cardHeader}>
                      <div className={styles.cardHeaderLeft}>
                        <span className={styles.challanLabel}>Challan</span>
                        <span className={styles.challanNumber}>#{challan.id}</span>
                      </div>
                      <div className={styles.cardHeaderRight}>
                        <div className={styles.issueDate}>
                          <span className={styles.issueDateLabel}>Issue Date</span>
                          <span className={styles.issueDateValue}>{challan.issueDate}</span>
                        </div>
                        <div
                          className={`${styles.checkboxWrapper} ${selectedChallans[challan.id] ? styles.checked : ''}`}
                          onClick={() => toggleChallanSelection(challan.id)}
                        >
                          {selectedChallans[challan.id] && (
                            <FontAwesomeIcon icon={faCheck} className={styles.checkIcon} />
                          )}
                        </div>
                      </div>
                    </div>

                    <div className={styles.cardBody}>
                      <div className={styles.cardBodyLeft}>
                        {challan.court_status && challan.court_status.toLowerCase() !== 'no' && (
                          <div className={styles.courtBadge}>
                            <FontAwesomeIcon icon={faGavel} />
                            <span>{challan.court_status}</span>
                          </div>
                        )}
                        <div className={styles.offenceLabel}>Offence:</div>
                        <div className={`${styles.offenceText} ${expandedChallans[challan.id] ? styles.offenceExpanded : ''}`}>
                          {challan.offence}
                        </div>
                        <span className={styles.offenceMore} onClick={() => toggleExpand(challan.id)}>
                          {expandedChallans[challan.id] ? '...Less' : '...More'}
                        </span>
                        <div className={styles.challanAmount}>
                          <span className={styles.amountLabel}>Challan Amount</span>
                          <div className={styles.amountValue}>₹{challan.amount?.toLocaleString()}</div>
                        </div>
                      </div>
                      <div className={styles.cardBodyRight}>
                        <div className={styles.challanImagePlaceholder}>
                          <span>📷</span>
                          <span className={styles.placeholderLabel}>Violation</span>
                        </div>
                      </div>
                    </div>

                    <div className={styles.cardFooter}>
                      <span className={styles.pdfLink} onClick={() => downloadChallanPDF(challan)}>
                        eCHALLAN PDF <FontAwesomeIcon icon={faExternalLinkAlt} />
                      </span>
                      <button
                        className={styles.payNowBtn}
                        onClick={() => handlePayment(challan)}
                        disabled={!agreedToTerms[challan.id]}
                      >
                        <FontAwesomeIcon icon={faExternalLinkAlt} />
                        PAY NOW
                      </button>
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* Court Challans Section */}
            {getCourtChallans().length > 0 && (
              <div className={styles.courtSection}>
                <div className={styles.sectionHeaderRow}>
                  <div className={styles.sectionTitleGroup}>
                    <div className={`${styles.sectionIcon} ${styles.courtSectionIcon}`}>
                      <FontAwesomeIcon icon={faGavel} />
                    </div>
                    <div className={styles.sectionTitleText}>
                      <h3 className={styles.sectionTitle}>Pending in Court ({getCourtChallans().length})</h3>
                      <p className={styles.sectionSubtitle}>Requires court appearance</p>
                    </div>
                  </div>
                  <button
                    className={`${styles.selectAllBtn} ${getCourtChallans().every(c => selectedChallans[c.id]) ? styles.selected : ''}`}
                    onClick={handleSelectAll}
                  >
                    <FontAwesomeIcon icon={faCheck} />
                    Select All
                  </button>
                </div>

                {getCourtChallans().map((challan, index) => (
                  <div
                    key={challan.id || index}
                    className={`${styles.challanCard} ${styles.courtCard} ${selectedChallans[challan.id] ? styles.selected : ''}`}
                  >
                    <div className={styles.cardHeader}>
                      <div className={styles.cardHeaderLeft}>
                        <span className={styles.challanLabel}>Challan</span>
                        <span className={styles.challanNumber}>#{challan.id}</span>
                      </div>
                      <div className={styles.cardHeaderRight}>
                        <div className={styles.issueDate}>
                          <span className={styles.issueDateLabel}>Issue Date</span>
                          <span className={styles.issueDateValue}>{challan.issueDate}</span>
                        </div>
                        <div
                          className={`${styles.checkboxWrapper} ${selectedChallans[challan.id] ? styles.checked : ''}`}
                          onClick={() => toggleChallanSelection(challan.id)}
                        >
                          {selectedChallans[challan.id] && (
                            <FontAwesomeIcon icon={faCheck} className={styles.checkIcon} />
                          )}
                        </div>
                      </div>
                    </div>

                    <div className={styles.cardBody}>
                      <div className={styles.cardBodyLeft}>
                        {challan.court_status && challan.court_status.toLowerCase() !== 'no' && (
                          <div className={styles.courtBadge}>
                            <FontAwesomeIcon icon={faGavel} />
                            <span>Yes this is the court challan</span>
                          </div>
                        )}
                        <div className={styles.offenceLabel}>Offence:</div>
                        <div className={`${styles.offenceText} ${expandedChallans[challan.id] ? styles.offenceExpanded : ''}`}>{challan.offence}</div>
                        <span className={styles.offenceMore} onClick={() => toggleExpand(challan.id)}>
                          {expandedChallans[challan.id] ? '...Less' : '...More'}
                        </span>
                        <div className={styles.challanAmount}>
                          <span className={styles.amountLabel}>Challan Amount</span>
                          <div className={styles.amountValue}>₹{challan.amount?.toLocaleString()}</div>
                        </div>
                      </div>
                      <div className={styles.cardBodyRight}>
                        <div className={`${styles.challanImagePlaceholder} ${styles.courtPlaceholder}`}>
                          <span>⚖️</span>
                          <span className={styles.placeholderLabel}>Court</span>
                        </div>
                      </div>
                    </div>

                    <div className={styles.cardFooter}>
                      <span className={`${styles.pdfLink} ${styles.courtReceiptLink}`} onClick={() => downloadChallanPDF(challan)}>
                        court challan pdf <FontAwesomeIcon icon={faExternalLinkAlt} />
                      </span>
                      <button
                        className={styles.payNowBtn}
                        onClick={() => handlePayment(challan)}
                        disabled={!agreedToTerms[challan.id]}
                      >
                        <FontAwesomeIcon icon={faExternalLinkAlt} />
                        PAY NOW
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Empty State for Pending */}
            {pendingChallans.length === 0 && (
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}>✅</div>
                <p className={styles.emptyText}>No pending challans found!</p>
              </div>
            )}

            {/* Disclaimer */}
            <div className={styles.disclaimer}>
              We do not have any association with any state RTO.
              Vehicleinfo about vehicle owners shown in the a...More
            </div>
          </>
        ) : (
          <>
            {/* Paid Challans */}
            {paidChallans.length > 0 ? (
              <>
                <div className={styles.sectionHeaderRow}>
                  <div className={styles.sectionTitleGroup}>
                    <div className={styles.sectionIcon}>
                      <FontAwesomeIcon icon={faCheck} />
                    </div>
                    <div className={styles.sectionTitleText}>
                      <h3 className={styles.sectionTitle}>Paid Challans ({paidChallans.length})</h3>
                      <p className={styles.sectionSubtitle}>Payment completed</p>
                    </div>
                  </div>
                </div>

                {paidChallans.map((challan, index) => (
                  <div key={challan.id || index} className={styles.challanCard}>
                    <div className={styles.cardHeader}>
                      <div className={styles.cardHeaderLeft}>
                        <span className={styles.challanLabel}>Challan</span>
                        <span className={styles.challanNumber}>#{challan.id}</span>
                      </div>
                      <div className={styles.cardHeaderRight}>
                        <div className={styles.issueDate}>
                          <span className={styles.issueDateLabel}>Issue Date</span>
                          <span className={styles.issueDateValue}>{challan.issueDate}</span>
                        </div>
                      </div>
                    </div>

                    <div className={styles.cardBody}>
                      <div className={styles.cardBodyLeft}>
                        <div className={styles.offenceLabel}>Offence:</div>
                        <div className={styles.offenceText}>{challan.offence}</div>
                        <div className={styles.challanAmount}>
                          <span className={styles.amountLabel}>Amount Paid</span>
                          <div className={`${styles.amountValue} ${styles.paid}`}>
                            ₹{challan.total_amount?.toLocaleString() || challan.amount?.toLocaleString()}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </>
            ) : (
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}>📋</div>
                <p className={styles.emptyText}>No paid challans found.</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Fixed Bottom Payment Button */}
      {activeTab === 'pending' && getSelectedCount() > 0 && (
        <div className={styles.fixedBottomBar}>
          <button
            className={styles.payAllButton}
            onClick={handlePayAll}
          >
            PAY {getSelectedCount()} CHALLAN{getSelectedCount() > 1 ? 'S' : ''} • ₹{getSelectedTotal().toLocaleString()}
          </button>
        </div>
      )}

      {/* Modals */}
      <RCDetailsModal />
      <TermsModal />
      <PaymentSuccessModal challans={paidChallanForModal} />
      <PayAllModal />
    </div>
  );
}

export default ChallanDetails;
